import { TCommentListItem } from 'components/CommentsWidget/types';
import {
  TCheckExistingInternalDocumentNumberQueryParams,
  TCheckExistingInternalDocumentNumberQueryResponse,
  TCreateInternalDocumentCommentPayload,
  TDeleteInternalDocumentCommentPayload,
  TGetInternalDocumentDetailParams,
  TUpdateDocumentAssigneeAndStatusPayload,
} from 'features/internal-document/common/types';
import {
  TIncomingInternalDocumentDetail,
  TUpdateIncomingInternalDocumentPayload,
} from 'features/internal-document/incoming/types';
import {
  TOutgoingInternalDocumentDetail,
  TUpdateOutgoingInternalDocumentPayload,
} from 'features/internal-document/outgoing/types';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';
import { StrictUnion } from 'utils/types';

const endpoint = `/internal-documents`;

export const internalDocumentApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      QueryTags.InternalDocumentComments,
      QueryTags.IncomingInternalDocumentList,
      QueryTags.OutgoingInternalDocumentList,
      QueryTags.InternalDocumentDetail,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetInternalDocumentDetail: builder.query<
        StrictUnion<
          TIncomingInternalDocumentDetail | TOutgoingInternalDocumentDetail
        >,
        TGetInternalDocumentDetailParams
      >({
        query: ({ documentCode, type }) => ({
          url: `${endpoint}/${type.toLowerCase()}/${documentCode}`,
        }),
        providesTags: [QueryTags.InternalDocumentDetail],
      }),

      CreateInternalDocumentComment: builder.mutation<
        void,
        TCreateInternalDocumentCommentPayload
      >({
        query: ({ documentCode, data }) => ({
          url: `${endpoint}/${documentCode}/comments`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.InternalDocumentComments,
          QueryTags.InternalDocumentDetail,
        ]),
      }),

      GetInternalDocumentComments: builder.query<TCommentListItem[], string>({
        query: documentCode => ({
          url: `${endpoint}/${documentCode}/comments`,
        }),
        providesTags: [QueryTags.InternalDocumentComments],
      }),

      DeleteInternalDocumentComment: builder.mutation<
        void,
        TDeleteInternalDocumentCommentPayload
      >({
        query: payload => ({
          url: `${endpoint}/${payload.documentCode}/comments/${payload.commentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.InternalDocumentComments]),
      }),

      DeleteInternalDocument: builder.mutation<void, string>({
        query: documentCode => ({
          url: `${endpoint}/${documentCode}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.IncomingInternalDocumentList,
          QueryTags.OutgoingInternalDocumentList,
        ]),
      }),

      UpdateDocumentAssigneeAndStatus: builder.mutation<
        void,
        TUpdateDocumentAssigneeAndStatusPayload
      >({
        query: ({ documentCode, data }) => ({
          url: `${endpoint}/${documentCode}/user/update`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.InternalDocumentDetail]),
      }),

      UpdateDocumentDetail: builder.mutation<
        string,
        StrictUnion<
          | TUpdateIncomingInternalDocumentPayload
          | TUpdateOutgoingInternalDocumentPayload
        >
      >({
        query: ({ documentCode, data }) => ({
          url: `${endpoint}/${documentCode}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.InternalDocumentDetail]),
      }),

      CheckExistingInternalDocumentNumber: builder.query<
        TCheckExistingInternalDocumentNumberQueryResponse,
        TCheckExistingInternalDocumentNumberQueryParams
      >({
        query: params => ({
          url: `${endpoint}/warning-document-number`,
          params,
        }),
      }),
    }),
  });

export const {
  useGetInternalDocumentDetailQuery,
  useCreateInternalDocumentCommentMutation,
  useGetInternalDocumentCommentsQuery,
  useDeleteInternalDocumentCommentMutation,
  useDeleteInternalDocumentMutation,
  useUpdateDocumentAssigneeAndStatusMutation,
  useUpdateDocumentDetailMutation,
  useCheckExistingInternalDocumentNumberQuery,
  useLazyCheckExistingInternalDocumentNumberQuery,
} = internalDocumentApi;
